import Phaser from "phaser";
import { Direction } from "@shared/types/entity";
import { addGridSpriteSheet, getNominalFrameSize } from "./gridSpriteSheet";

/**
 * Configuración de los sprites de body:
 * - 4 filas (abajo, arriba, izquierda, derecha)
 * - 6 columnas (layout estándar AO; ver BODY_SHEET_LAYOUTS para excepciones)
 *
 * Las dimensiones de cada body se detectan automáticamente al cargar.
 */
export const BODY_COLUMNS = 6;
export const BODY_ROWS = 4;

/**
 * Layouts por sheet para bodies que NO siguen la grilla estándar 6×4 (típico en
 * bodies de NPC importados con otra cantidad de frames de walk).
 *
 * - `columns`: columnas reales del PNG (el ancho debe ser múltiplo de esto).
 * - `lateralColumns` (opcional): frames de walk en las filas LEFT/RIGHT si
 *   difieren de las verticales. Default: en el layout estándar de 6 las
 *   laterales usan 5 (col 5 vacía, convención AO); en layouts custom se asume
 *   que TODAS las filas usan `columns` completas.
 *
 * Un body que se ve "picado"/desfasado al caminar y cuyo ancho no corta limpio
 * en 6 probablemente necesita entry acá.
 */
const BODY_SHEET_LAYOUTS: Record<number, { columns: number; lateralColumns?: number }> = {
  19: { columns: 4 },
  // NPC bodies (criaturas, ids 201+): sheets 4×4 (4 frames de walk por dirección).
  201: { columns: 4 },
  202: { columns: 4 },
  203: { columns: 4 },
  204: { columns: 4 },
  // Criaturas hostiles importadas (4×N, N verificado a ojo — ver
  // graficos clasif/asdf/hostile_bodies_report.md). Si alguna camina "picada",
  // ajustar su `columns` (y si las laterales tienen 1 frame menos, sumar lateralColumns).
  205: { columns: 5 }, // cave_dragon
  206: { columns: 8 }, // cave_golem
  207: { columns: 6 }, // cave_minispider
  208: { columns: 5 }, // cave_troll
  209: { columns: 8 }, // desert_creature
  210: { columns: 8 }, // desert_mummy
  211: { columns: 8 }, // forest_chicken
  212: { columns: 8 }, // forest_goblin
  213: { columns: 6 }, // forest_gorilla
  214: { columns: 8 }, // forest_pig
  215: { columns: 4 }, // forest_plant
  216: { columns: 4 }, // forest_spider
  217: { columns: 4 }, // forest_spider2
  218: { columns: 4 }, // forest_spider3
  219: { columns: 4 }, // forest_tiger
  220: { columns: 4 }, // golem2_forest
  221: { columns: 8 }, // golem2_gold
  222: { columns: 8 }, // golem2_silver
  223: { columns: 8 }, // golem2_water
  224: { columns: 5 }, // golem3_gold
  225: { columns: 5 }, // golem3_iiron
  226: { columns: 8 }, // golem3_water
  227: { columns: 8 }, // golem_fire
  228: { columns: 6 }, // golem_fire2 (BMP)
  229: { columns: 8 }, // golem_fire2 (png)
  230: { columns: 8 }, // golem_lava
  231: { columns: 8 }, // golem_mud
  232: { columns: 8 }, // golem_silver
  233: { columns: 8 }, // golem_water
  234: { columns: 8 }, // golem_winged
  235: { columns: 6 }, // graveyard_ghost
  236: { columns: 6 }, // graveyard_shadow
  237: { columns: 8 }, // graveyard_zombie
  238: { columns: 8 }, // medusa
  239: { columns: 6 }, // ogre_general
  240: { columns: 6 }, // ogre_mountain
  241: { columns: 6 }, // ogre_warrior
  242: { columns: 6 }, // orc_wizard
  243: { columns: 6 }, // skeleton_archer
  244: { columns: 6 }, // skeleton_mage
  245: { columns: 6 }, // skeleton_warrior
  246: { columns: 6 }, // snow_beast
  247: { columns: 5 }, // snow_golem
  248: { columns: 6 }, // snow_gorilla
  249: { columns: 4 }, // snow_wolf
  250: { columns: 8 }, // snow_yeti
  251: { columns: 6 }, // tower_warrior
  252: { columns: 4 }, // felino (era npc3.bmp)
  // Batch animals
  253: { columns: 9 }, // crab
  254: { columns: 4 }, // fish_piranha
  255: { columns: 4 }, // kraken
  256: { columns: 3 }, // manta
  257: { columns: 8 }, // ray
  258: { columns: 4 }, // shark_gray
  259: { columns: 4 }, // shark_white
  260: { columns: 4 }, // turtle
  261: { columns: 3 }, // bat (murciélago 3×4)
  262: { columns: 8 }, // scorpion (escorpión 8×4)
};

/** Columnas reales del sheet de un body (default: estándar 6). */
export function getBodyColumns(bodyId: number): number {
  return BODY_SHEET_LAYOUTS[bodyId]?.columns ?? BODY_COLUMNS;
}

/** Frames de walk de las filas laterales (LEFT/RIGHT) de un body. */
export function getBodyLateralColumns(bodyId: number): number {
  const layout = BODY_SHEET_LAYOUTS[bodyId];
  if (layout) return layout.lateralColumns ?? layout.columns;
  return BODY_COLUMNS - 1; // estándar AO: col 5 vacía en filas laterales
}

/**
 * Tempo del walk para body, weapon y shield. Se escala por moveSpeed vía
 * anims.timeScale en el render.
 *
 * Referencia: el cliente oficial de AO usa ~18fps (graficos.ini: ciclo vertical
 * 333ms/6 frames, lateral 277ms/5 frames → ~55.4 ms/frame). En Aureum se eligió
 * LA MITAD de ese tempo (9fps, ~111 ms/frame) por preferencia visual.
 */
export const WALK_ANIM_FRAME_RATE = 9;

// Caché de dimensiones de frame por bodyId (calculadas automáticamente)
const bodyFrameDimensionsCache: Map<number, { width: number; height: number }> =
  new Map();

// Exportar para compatibilidad (valores de referencia, no usados para carga)
export const BODY_FRAME_WIDTH = 43;
export const BODY_FRAME_HEIGHT = 64;

// Ajustes manuales de offset por bodyId si alguno lo necesita
// Valor en píxeles: positivo = derecha, negativo = izquierda
const MANUAL_CENTER_OFFSETS: Record<number, number> = {
  // Ajuste fino ADITIVO sobre el auto-centrado (px; +derecha, -izquierda). Para nudge manual de
  // un body puntual cuando el centroide no coincide con el centro percibido.
};

type DirectionAnimKey = "down" | "up" | "left" | "right";

function getDirectionKey(dir: Direction): DirectionAnimKey {
  switch (dir) {
    case Direction.DOWN:
      return "down";
    case Direction.UP:
      return "up";
    case Direction.LEFT:
      return "left";
    case Direction.RIGHT:
      return "right";
    default:
      return "down";
  }
}

export function getBodySpriteKey(bodyId: number): string {
  return `body${bodyId}`;
}

/** Carpeta base de los spritesheets de walk de los bodies (vestimentas/armaduras). */
export const BODY_ANIMS_BASE_PATH = 'assets/entities/body/anims';

/**
 * Naked bodies (cuerpos desnudos por raza/sexo, IDs 101–112). A diferencia del
 * resto de los bodies (vestimentas/armaduras) cuyo spritesheet vive en
 * `assets/entities/body/anims/body<id>.png`, estos cargan desde
 * `assets/entities/body/races/<raza>_<sexo>.png` (nombres semánticos). El mapeo
 * raza/sexo → id está en `NAKED_BODY_CONFIG` (`shared/types/character.ts`).
 *
 * Solo cambia el ORIGEN del asset: una vez cargado bajo la key `body<id>`, el
 * pipeline (spritesheet, animaciones, render) los trata igual que cualquier body.
 */
export const NAKED_BODY_ASSET_PATHS: Record<number, string> = {
  101: 'assets/entities/body/races/human_male.png',
  102: 'assets/entities/body/races/human_female.png',
  103: 'assets/entities/body/races/elf_male.png',
  104: 'assets/entities/body/races/elf_female.png',
  105: 'assets/entities/body/races/orc_male.png',
  106: 'assets/entities/body/races/orc_female.png',
  107: 'assets/entities/body/races/drow_male.png',
  108: 'assets/entities/body/races/drow_female.png',
  109: 'assets/entities/body/races/dwarf_male.png',
  110: 'assets/entities/body/races/dwarf_female.png',
  111: 'assets/entities/body/races/gnome_male.png',
  112: 'assets/entities/body/races/gnome_female.png',
};

/**
 * NPC bodies (criaturas usadas por mobs/NPCs, ids 201+). A diferencia de los bodies de
 * vestimenta/armadura (anims/body<id>.png), su spritesheet vive en `assets/entities/npcs/npc<n>.png`
 * (organizado aparte). Solo cambia el ORIGEN del asset: una vez cargado bajo la key `body<id>`, el
 * pipeline (spritesheet 4×4, animaciones, render) los trata igual que cualquier body.
 */
export const NPC_BODY_ASSET_PATHS: Record<number, string> = {
  201: 'assets/entities/npcs/npc1.png',
  202: 'assets/entities/npcs/npc2.png',
  203: 'assets/entities/npcs/npc3.png',
  204: 'assets/entities/npcs/npc4.png',
  // Criaturas hostiles importadas (npcs/hostile/npc<n>.png; bodyId = 200 + n).
  // Ver graficos clasif/asdf/hostile_bodies_report.md. Layout 4×N en BODY_SHEET_LAYOUTS.
  205: 'assets/entities/npcs/hostile/npc5.png', // cave_dragon
  206: 'assets/entities/npcs/hostile/npc6.png', // cave_golem
  207: 'assets/entities/npcs/hostile/npc7.png', // cave_minispider
  208: 'assets/entities/npcs/hostile/npc8.png', // cave_troll
  209: 'assets/entities/npcs/hostile/npc9.png', // desert_creature
  210: 'assets/entities/npcs/hostile/npc10.png', // desert_mummy
  211: 'assets/entities/npcs/hostile/npc11.png', // forest_chicken
  212: 'assets/entities/npcs/hostile/npc12.png', // forest_goblin
  213: 'assets/entities/npcs/hostile/npc13.png', // forest_gorilla
  214: 'assets/entities/npcs/hostile/npc14.png', // forest_pig
  215: 'assets/entities/npcs/hostile/npc15.png', // forest_plant
  216: 'assets/entities/npcs/hostile/npc16.png', // forest_spider
  217: 'assets/entities/npcs/hostile/npc17.png', // forest_spider2
  218: 'assets/entities/npcs/hostile/npc18.png', // forest_spider3
  219: 'assets/entities/npcs/hostile/npc19.png', // forest_tiger
  220: 'assets/entities/npcs/hostile/npc20.png', // golem2_forest
  221: 'assets/entities/npcs/hostile/npc21.png', // golem2_gold
  222: 'assets/entities/npcs/hostile/npc22.png', // golem2_silver
  223: 'assets/entities/npcs/hostile/npc23.png', // golem2_water
  224: 'assets/entities/npcs/hostile/npc24.png', // golem3_gold
  225: 'assets/entities/npcs/hostile/npc25.png', // golem3_iiron
  226: 'assets/entities/npcs/hostile/npc26.png', // golem3_water
  227: 'assets/entities/npcs/hostile/npc27.png', // golem_fire
  228: 'assets/entities/npcs/hostile/npc28.png', // golem_fire2 (BMP)
  229: 'assets/entities/npcs/hostile/npc29.png', // golem_fire2 (png)
  230: 'assets/entities/npcs/hostile/npc30.png', // golem_lava
  231: 'assets/entities/npcs/hostile/npc31.png', // golem_mud
  232: 'assets/entities/npcs/hostile/npc32.png', // golem_silver
  233: 'assets/entities/npcs/hostile/npc33.png', // golem_water
  234: 'assets/entities/npcs/hostile/npc34.png', // golem_winged
  235: 'assets/entities/npcs/hostile/npc35.png', // graveyard_ghost
  236: 'assets/entities/npcs/hostile/npc36.png', // graveyard_shadow
  237: 'assets/entities/npcs/hostile/npc37.png', // graveyard_zombie
  238: 'assets/entities/npcs/hostile/npc38.png', // medusa
  239: 'assets/entities/npcs/hostile/npc39.png', // ogre_general
  240: 'assets/entities/npcs/hostile/npc40.png', // ogre_mountain
  241: 'assets/entities/npcs/hostile/npc41.png', // ogre_warrior
  242: 'assets/entities/npcs/hostile/npc42.png', // orc_wizard
  243: 'assets/entities/npcs/hostile/npc43.png', // skeleton_archer
  244: 'assets/entities/npcs/hostile/npc44.png', // skeleton_mage
  245: 'assets/entities/npcs/hostile/npc45.png', // skeleton_warrior
  246: 'assets/entities/npcs/hostile/npc46.png', // snow_beast
  247: 'assets/entities/npcs/hostile/npc47.png', // snow_golem
  248: 'assets/entities/npcs/hostile/npc48.png', // snow_gorilla
  249: 'assets/entities/npcs/hostile/npc49.png', // snow_wolf
  250: 'assets/entities/npcs/hostile/npc50.png', // snow_yeti
  251: 'assets/entities/npcs/hostile/npc51.png', // tower_warrior
  252: 'assets/entities/npcs/hostile/npc52.png', // felino (era npc3.bmp)
  // Batch animals (criaturas acuáticas/anfibias; ver convertir_hostiles.py).
  253: 'assets/entities/npcs/hostile/npc53.png', // crab (cangrejo)
  254: 'assets/entities/npcs/hostile/npc54.png', // fish_piranha (piraña)
  255: 'assets/entities/npcs/hostile/npc55.png', // kraken (calamar)
  256: 'assets/entities/npcs/hostile/npc56.png', // manta (mantarraya)
  257: 'assets/entities/npcs/hostile/npc57.png', // ray (cangrejo/artrópodo)
  258: 'assets/entities/npcs/hostile/npc58.png', // shark_gray (tiburón gris)
  259: 'assets/entities/npcs/hostile/npc59.png', // shark_white (tiburón blanco)
  260: 'assets/entities/npcs/hostile/npc60.png', // turtle (tortuga)
  261: 'assets/entities/npcs/hostile/npc61.png', // bat (murciélago)
  262: 'assets/entities/npcs/hostile/npc62.png', // scorpion (escorpión)
};

/**
 * Ids de bodies de vestimenta/armadura que TIENEN walk-sheet en
 * `assets/entities/body/anims/body<id>.png`. (Los naked 101–112 tienen su registro
 * `NAKED_BODY_ASSET_PATHS`; los NPC 201+ el suyo `NPC_BODY_ASSET_PATHS`.)
 *
 * IMPORTANTE: al agregar un `body<id>.png` nuevo en `anims/`, sumá su id acá — si no,
 * no se precarga ni se le crean animaciones. Antes se iteraba `1..112` a ciegas: ~80
 * ids inexistentes → un 404 por cada uno al precargar + flood de "Frame N not found"
 * al intentar cortarlos/animarlos. Esta lista es la fuente de verdad de qué existe.
 */
export const VESTIMENT_BODY_IDS: number[] = [
  1, 2, 3, 20, 21, 22, 23, 30, 31, 32, 33, 34, 35, 36, 37, 38, 39, 40, 41, 42, 43,
];

/** Ids de los NPC bodies (para appendear a las listas de preload/setup de GameScene). */
export const NPC_BODY_IDS: number[] = Object.keys(NPC_BODY_ASSET_PATHS).map(Number);

/** Ids de naked bodies (cuerpos base por raza/sexo) que tienen asset real en `races/`. */
export const NAKED_BODY_IDS: number[] = Object.keys(NAKED_BODY_ASSET_PATHS).map(Number);

/**
 * Todos los body IDs que tienen asset y por ende conviene precargar/precalentar:
 * vestimentas + naked + NPC. Reemplaza el viejo `for (i=1..112)` que pedía ~80 archivos
 * inexistentes. Ver `preloadBodySprites`/`setupBodySpritesheets`.
 */
export function getAllExistingBodyIds(): number[] {
  return [...VESTIMENT_BODY_IDS, ...NAKED_BODY_IDS, ...NPC_BODY_IDS];
}

export function getBodyAnimKey(bodyId: number, dir: Direction): string {
  const dirKey = getDirectionKey(dir);
  return `body${bodyId}_${dirKey}`;
}

/**
 * Advierte UNA sola vez (por animKey) que la animación de caminar de un body está
 * rota (inexistente / sin frames / play() tiró excepción). Lo usan Player/NpcSync
 * para diagnosticar QUÉ bodyId no tiene anim sin spamear la consola por frame.
 */
const _warnedBodyAnims = new Set<string>();
export function warnBadBodyAnim(animKey: string, bodyId: number, reason: string): void {
  if (_warnedBodyAnims.has(animKey)) return;
  _warnedBodyAnims.add(animKey);
  // eslint-disable-next-line no-console
  console.warn(
    `[BodyAnim] animación de caminar ROTA (${reason}): key=${animKey} bodyId=${bodyId} — fallback a frame estático (no crashea el loop).`
  );
}

/**
 * Devuelve el índice de frame para el frame "idle" (columna 0) de una dirección.
 * Pasar `bodyId` para bodies con layout no estándar (los índices son row-major
 * por las columnas REALES del sheet); sin bodyId asume la grilla estándar de 6.
 */
export function getBodyIdleFrame(dir: Direction, bodyId?: number): number {
  let row = 0;
  switch (dir) {
    case Direction.DOWN:
      row = 0;
      break;
    case Direction.UP:
      row = 1;
      break;
    case Direction.LEFT:
      row = 2;
      break;
    case Direction.RIGHT:
      row = 3;
      break;
    default:
      row = 0;
      break;
  }

  const columns = bodyId != null ? getBodyColumns(bodyId) : BODY_COLUMNS;
  return row * columns; // columna 0
}

/**
 * Obtiene las dimensiones de frame para un bodyId específico.
 * Las dimensiones se detectan automáticamente cuando se carga el sprite.
 */
export function getBodyFrameDimensions(bodyId: number): {
  width: number;
  height: number;
} {
  const cached = bodyFrameDimensionsCache.get(bodyId);
  if (cached) {
    return cached;
  }
  // Fallback si no está cacheado (no debería pasar si se usa setupBodySpritesheets)
  return { width: BODY_FRAME_WIDTH, height: BODY_FRAME_HEIGHT };
}

/**
 * Offset X para centrar el body en el tile. Es el auto-centrado (criaturas) MÁS un ajuste
 * fino manual aditivo (`MANUAL_CENTER_OFFSETS`), por si algún body necesita correrse 1-2px
 * sobre lo que calcula el centroide. Para bodies sin auto-offset (players), el manual actúa
 * como offset absoluto. (+derecha, -izquierda).
 */
export function getBodyCenterOffset(bodyId: number): number {
  return MANUAL_CENTER_OFFSETS[bodyId] ?? 0;
}


/**
 * Pre-carga los bodies como imágenes para poder detectar sus dimensiones.
 * Después de preload, llamar a setupBodySpritesheets() para convertirlos a spritesheets.
 */
export function preloadBodySprites(
  scene: Phaser.Scene,
  bodyIds: number[]
): void {
  for (const id of bodyIds) {
    const key = getBodySpriteKey(id);

    // Evitar registrar dos veces
    if (scene.textures.exists(key)) continue;

    // Cargar como imagen primero (para detectar dimensiones). Los naked bodies
    // (101–112) viven en assets/entities/body/races/; los NPC bodies (201+) en
    // assets/entities/npcs/; el resto (vestimentas/armaduras) en
    // assets/entities/body/anims/body<id>.png.
    const path =
      NAKED_BODY_ASSET_PATHS[id] ?? NPC_BODY_ASSET_PATHS[id] ?? `${BODY_ANIMS_BASE_PATH}/${key}.png`;
    scene.load.image(key, path);
  }
}

/**
 * Devuelve un canvas con el fondo NEGRO de un sheet convertido a transparente. Usado por los
 * NPC bodies (sheets estilo AO con fondo negro sólido). Keyea sólo el negro casi-puro
 * (cada canal ≤ threshold) para no agujerear el pelaje oscuro de las criaturas.
 */
function makeBlackTransparent(source: HTMLImageElement, threshold = 16): HTMLCanvasElement {
  const w = source.width;
  const h = source.height;
  const canvas = document.createElement('canvas');
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext('2d');
  if (!ctx) return canvas;
  ctx.drawImage(source, 0, 0);
  const img = ctx.getImageData(0, 0, w, h);
  const d = img.data;
  for (let i = 0; i < d.length; i += 4) {
    if (d[i] <= threshold && d[i + 1] <= threshold && d[i + 2] <= threshold) {
      d[i + 3] = 0;
    }
  }
  ctx.putImageData(img, 0, 0);
  return canvas;
}

// Track which bodies have already been set up as spritesheets to prevent re-creation
const setupBodyIds = new Set<number>();

/**
 * Convierte las imágenes cargadas en spritesheets con dimensiones correctas.
 * DEBE llamarse en create() después de que preload haya terminado.
 *
 * Este método detecta automáticamente las dimensiones de cada sprite
 * y crea los frames correctamente para que setOrigin() funcione bien.
 * 
 * RENDERING FIX: This function now tracks which bodies have been set up
 * to prevent texture recreation that could cause flickering.
 */
export function setupBodySpritesheets(
  scene: Phaser.Scene,
  bodyIds: number[]
): void {
  for (const id of bodyIds) {
    const key = getBodySpriteKey(id);

    const texture = scene.textures.get(key);
    if (!texture || texture.key === "__MISSING") {
      continue;
    }

    // =====================================================================
    // RENDERING FIX: Check if the texture is ACTUALLY configured as a spritesheet
    // with the correct number of frames. The setupBodyIds Set can persist across
    // scene reloads, but the textures get reloaded as simple images.
    // A properly configured spritesheet has BODY_ROWS * BODY_COLUMNS frames.
    // =====================================================================
    const columns = getBodyColumns(id);
    const expectedFrameCount = BODY_ROWS * columns;
    const actualFrameCount = texture.frameTotal;
    
    // If already set up AND has correct frame count, skip
    if (setupBodyIds.has(id) && actualFrameCount >= expectedFrameCount) {
      continue;
    }
    
    // If it was marked as set up but doesn't have correct frames, remove from set
    if (setupBodyIds.has(id) && actualFrameCount < expectedFrameCount) {
      setupBodyIds.delete(id);
      console.log(`[BodySpriteManager] Body ${id} was marked as set up but only has ${actualFrameCount} frames (expected ${expectedFrameCount}), reconfiguring...`);
    }

    // Obtener dimensiones de la imagen original
    const source = texture.getSourceImage();
    const imgWidth = source.width;
    const imgHeight = source.height;

    // Dimensiones nominales del frame (para centrado/origen). El frame real de
    // cada celda lo determina addGridSpriteSheet con bordes redondeados.
    const { width: frameWidth, height: frameHeight } = getNominalFrameSize(
      imgWidth,
      imgHeight,
      columns,
      BODY_ROWS
    );

    // Guardar dimensiones en caché
    bodyFrameDimensionsCache.set(id, {
      width: frameWidth,
      height: frameHeight,
    });

    // Crear nueva textura cortando por bordes redondeados (sin drift si el PNG
    // no es múltiplo exacto de BODY_COLUMNS×BODY_ROWS). Remueve la key existente.
    // Los NPC bodies (sheets AO con fondo negro) se keyean a transparente antes de cortar.
    // Los NPC bodies se keyean a transparente (fondo negro → alpha 0). El centrado depende del
    // arte (la criatura debe estar centrada en su celda): el engine los dibuja en renderX igual
    // que los bodys comunes; el medio-píxel no se puede compensar con offsets enteros.
    const imageElement = source as HTMLImageElement;
    const sheetSource: HTMLImageElement | HTMLCanvasElement =
      NPC_BODY_ASSET_PATHS[id] !== undefined ? makeBlackTransparent(imageElement) : imageElement;
    const newTexture = addGridSpriteSheet(
      scene,
      key,
      sheetSource,
      columns,
      BODY_ROWS
    );

    if (newTexture) {
      setupBodyIds.add(id);
      const parity = frameWidth % 2 === 0 ? "even" : "odd";
      console.log(
        `[BodySpriteManager] Body ${id}: ${imgWidth}x${imgHeight} → frame ${frameWidth}x${frameHeight} (${parity})`
      );
    }
  }
}

/**
 * Garantiza que un body esté LISTO para renderizar (spritesheet cortado + animaciones creadas)
 * ANTES de que un sprite lo use. Es idempotente y barato: si ya está cortado/animado no hace nada.
 *
 * Por qué existe: los bodies se precortan en un warmup repartido en frames (`runWarmupChunked` en
 * `GameScene.create`, que es `async`). Phaser NO espera a que ese `create()` async termine: arranca
 * `update()` (→ render de players/NPCs) mientras el warmup sigue cortando. Un body que se renderiza
 * ANTES de estar cortado se ve como la LÁMINA ENTERA (grilla de todas sus poses) y `setFrame(idx)`
 * tira "Texture bodyN has no frame idx" en cada frame (los idle re-setean el frame por tick →
 * spam sin parar). Peor: si el warmup se aborta (translado) o una excepción previa en `create()` lo
 * saltea, el body NO se corta NUNCA y queda roto de forma permanente.
 *
 * Llamando esto en cada punto donde se crea/re-texturiza/anima un body (Player/NpcSyncController), el
 * render se AUTO-REPARA: el primer frame que toca un body no-listo lo corta y sigue normal. El warmup
 * queda como mera optimización (pre-calentamiento), no como requisito de correctitud.
 */
const _uncuttableBodies = new Set<number>();
export function ensureBodyReady(scene: Phaser.Scene, bodyId: number): void {
  // Anti-reintento por frame: un body cuya textura existe pero NUNCA se puede cortar
  // (imagen 404 registrada vacía, dimensiones inválidas) haría correr setup+anims en
  // CADA frame para CADA entidad que lo use (costo sostenido + reintentos). Una vez que
  // detectamos que no se puede preparar, lo marcamos y salimos por el fast path.
  if (_uncuttableBodies.has(bodyId)) return;

  const key = getBodySpriteKey(bodyId);
  // Si el asset todavía no se precargó (imagen), no se puede cortar sincrónicamente: el preload/
  // warmup lo hará y un futuro frame volverá a pasar por acá. No forzamos carga (es async).
  if (!scene.textures.exists(key)) return;

  const expectedFrameCount = BODY_ROWS * getBodyColumns(bodyId);
  const readyFrames = scene.textures.get(key).frameTotal >= expectedFrameCount;
  const readyAnims = scene.anims.exists(getBodyAnimKey(bodyId, Direction.DOWN));
  if (readyFrames && readyAnims) return; // ya listo → fast path (chequeo O(1) por frame)

  // Cortar el sheet (idempotente: se saltea si ya estaba) y crear sus animaciones (ensureAnim ya
  // chequea `anims.exists`). Barato para un solo body (~3ms), y sólo ocurre la primera vez.
  setupBodySpritesheets(scene, [bodyId]);
  createBodyAnimations(scene, [bodyId], WALK_ANIM_FRAME_RATE);

  // Si tras intentar cortar sigue sin frames suficientes, la imagen es inservible (404/dims malas):
  // marcarlo para no reintentar cada frame. El render cae a frame estático vía el guard del caller.
  if (scene.textures.get(key).frameTotal < expectedFrameCount) {
    _uncuttableBodies.add(bodyId);
  }
}

/**
 * Crea las animaciones de caminar para cada body y dirección.
 * DEBE llamarse después de setupBodySpritesheets.
 */
export function createBodyAnimations(
  scene: Phaser.Scene,
  bodyIds: number[],
  frameRate: number = 8
): void {
  for (const id of bodyIds) {
    const key = getBodySpriteKey(id);

    // Verificar que la textura existe
    if (!scene.textures.exists(key)) continue;

    // GUARD anti-flood: sólo generar anims si el sheet está REALMENTE cortado en frames
    // (frameTotal suficiente). Si la textura existe pero no se pudo cortar (imagen 404 /
    // dimensiones inválidas / carrera con el warmup), llamar `generateFrameNumbers` haría
    // que Phaser loguee "Frame N not found in texture" por CADA índice → inundación de la
    // consola + costo por frame (ensureBodyReady reintenta). El render cae a frame estático
    // vía el guard de Player/NpcSyncController. Ver ensureBodyReady.
    const expectedFrameCount = BODY_ROWS * getBodyColumns(id);
    if (scene.textures.get(key).frameTotal < expectedFrameCount) continue;

    // Convención BH: el walk recorre la fila COMPLETA empezando en col 0 (la col 0
    // es a la vez idle y primer frame del walk). En el layout estándar down/up usan
    // 6 frames (cols 0..5) y left/right 5 (col 5 vacía); en layouts custom
    // (BODY_SHEET_LAYOUTS) cada fila usa sus columnas reales.
    const columns = getBodyColumns(id);
    const lateralColumns = getBodyLateralColumns(id);

    // Down (fila 0) / Up (fila 1): columnas 0..columns-1
    ensureAnim(scene, getBodyAnimKey(id, Direction.DOWN), key, 0, 0, columns - 1, columns, frameRate);
    ensureAnim(scene, getBodyAnimKey(id, Direction.UP), key, 1, 0, columns - 1, columns, frameRate);

    // Left (fila 2) / Right (fila 3): columnas 0..lateralColumns-1
    ensureAnim(scene, getBodyAnimKey(id, Direction.LEFT), key, 2, 0, lateralColumns - 1, columns, frameRate);
    ensureAnim(scene, getBodyAnimKey(id, Direction.RIGHT), key, 3, 0, lateralColumns - 1, columns, frameRate);
  }
}

function ensureAnim(
  scene: Phaser.Scene,
  animKey: string,
  spriteKey: string,
  row: number,
  colStart: number,
  colEnd: number,
  columns: number,
  frameRate: number
): void {
  if (scene.anims.exists(animKey)) return;

  const startIndex = row * columns + colStart;
  const endIndex = row * columns + colEnd;

  // generateFrameNumbers SALTEA los índices que la textura no tiene: si el sheet
  // de este body está mal cortado (imagen más chica de lo que declaran sus columnas)
  // o el rango es inválido (colEnd < colStart → columns=0), devuelve un array VACÍO.
  // Crear una anim con 0 frames NO falla acá, pero después `play()` crashea leyendo
  // `currentFrame.duration` de undefined y rompe el update() entero. Por eso: si no
  // hay frames, NO creamos la anim (el render cae a frame estático vía el guard de
  // Player/NpcSyncController) y avisamos una vez para diagnosticar el sheet culpable.
  const frames = scene.anims.generateFrameNumbers(spriteKey, {
    start: startIndex,
    end: endIndex,
  });
  if (frames.length === 0) {
    warnBadBodyAnim(animKey, -1, `sheet-sin-frames(${spriteKey} ${startIndex}..${endIndex})`);
    return;
  }

  scene.anims.create({
    key: animKey,
    frames,
    frameRate,
    repeat: -1,
  });
}
